import { Input, Button, DatePicker, Select, Form as AntForm, Col, Row } from 'antd';
import styled from 'styled-components';
import { Controller, useFieldArray, Control } from 'react-hook-form';
import { LabeledValue } from 'antd/es/select';
import { OptionGroupData, OptionData } from 'rc-select/lib/interface';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { HSMSSelectOption } from '@components/shared';
import { HSMSDatePickerField } from './HSMSDatePicker';

const { Item: FormItem } = AntForm;
const { Option } = Select;
const { RangePicker } = DatePicker;

const DynamicList = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;

  .itemSet {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  .first-dynamic-delete-button {
    margin-top: 24px;
  }
`;

const RemoveIcon = styled.div`
  z-index: 100;
  height: 25px;
  margin-bottom: 20px;
`;

const HSMSDynamicInputFormGroupButtonWrapper = styled.div`
  padding-left: 16px;
`;

const HSMSDynamicFormItem = styled(FormItem)``;

interface HSMSDynamicInputFormGroupInputFields {
  label: string;
  name: string;
  min?: number;
  type?: string;
  options?: HSMSSelectOption[];
  loading?: boolean;
  generateOptions?: (fieldIndex: number) => HSMSSelectOption[];
  hidden?: boolean;
  span?: number;
  style?: React.CSSProperties;
  onSelect?: (value: string | number | LabeledValue, option: OptionData | OptionGroupData, filedIndex: number) => void;
}

interface HSMSDynamicInputFormGroupProps {
  name: string;
  inputFields: HSMSDynamicInputFormGroupInputFields[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValues?: any;
}

const renderField = (inputField: HSMSDynamicInputFormGroupInputFields, fieldIndex: number): React.ReactElement => {
  if (inputField.type === 'date') {
    return <HSMSDatePickerField />;
  }
  if (inputField.type === 'range') {
    return <RangePicker format="YYYY-MM-DD" placeholder={['Start Time', 'End Time']} />;
  }
  if (inputField.type === 'number') {
    return <Input type={inputField.type || 'number'} />;
  }
  if (inputField.type === 'select') {
    let options: HSMSSelectOption[] = [];

    if (inputField.options) {
      options = inputField.options;
    } else if (inputField.generateOptions) {
      options = inputField.generateOptions(fieldIndex);
    }

    return (
      <Select
        loading={inputField.loading}
        style={inputField.style || { width: '170px' }}
        onSelect={(value, option) => {
          if (inputField.onSelect) {
            inputField.onSelect(value, option, fieldIndex);
          }
        }}
      >
        {options.map((option) => (
          <Option key={option.label} value={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>
    );
  }

  return <Input type={inputField.type || 'text'} />;
};

export const HSMSDynamicInputFormGroup = ({
  name,
  inputFields,
  control,
  defaultValues,
}: HSMSDynamicInputFormGroupProps) => {
  const { fields, append, remove } = useFieldArray({ control, name });
  return (
    <>
      <div>
        {fields.map((item, index) => (
          <DynamicList key={item.id}>
            <Row className="itemSet" gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
              {inputFields.map((inputField) => {
                if (inputField.hidden) {
                  return (
                    <Controller
                      as={<input hidden />}
                      name={`${name}[${index}].${inputField.name}`}
                      defaultValue={item[inputField.name]}
                      control={control}
                      key={inputField.name}
                    />
                  );
                }
                return (
                  <Col
                    key={inputField.name}
                    span={inputField.span ? inputField.span : Math.floor(22 / inputFields.length)}
                  >
                    <HSMSDynamicFormItem
                      labelCol={{ span: 24 }}
                      key={item.id}
                      label={index === 0 ? inputField.label : ''}
                    >
                      <Controller
                        as={renderField(inputField, index)}
                        name={`${name}[${index}].${inputField.name}`}
                        min={inputField.min}
                        defaultValue={fields[index][inputField.name]}
                        control={control}
                      />
                    </HSMSDynamicFormItem>
                  </Col>
                );
              })}
              {fields.length > 1 && (
                <Col span={2}>
                  <RemoveIcon>
                    <MinusCircleOutlined
                      className={`dynamic-delete-button ${index === 0 && 'first-dynamic-delete-button'}`}
                      onClick={() => {
                        remove(index);
                      }}
                    />
                  </RemoveIcon>
                </Col>
              )}
            </Row>
          </DynamicList>
        ))}
      </div>
      <HSMSDynamicInputFormGroupButtonWrapper>
        <Button
          type="dashed"
          onClick={() => {
            append({ id: Math.random(), ...defaultValues });
          }}
        >
          <PlusOutlined /> Add field
        </Button>
      </HSMSDynamicInputFormGroupButtonWrapper>
    </>
  );
};
